import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiHeaders } from '../utils/apiHelpers';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Search, Filter, Check, X, AlertTriangle,
  FileText, Clock, CheckCircle, TrendingDown, Package,
  RefreshCw, ChevronDown, ChevronUp, Calendar, Euro
} from 'lucide-react';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const ResiFornitorePage = () => {
  const [resi, setResi] = useState([]);
  const [statistiche, setStatistiche] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFornitore, setFilterFornitore] = useState('');
  const [filterStato, setFilterStato] = useState('tutti'); // tutti, in_attesa, ricevuta
  const [expandedReso, setExpandedReso] = useState(null);
  const [updatingNC, setUpdatingNC] = useState(null);

  // Carica resi
  const loadResi = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterFornitore) params.append('fornitore', filterFornitore);
      params.append('limit', '100');
      
      const res = await axios.get(`${API}/gestione/resi-fornitore?${params}`, {
        headers: getApiHeaders()
      });
      
      if (res.data.success) {
        let resiData = res.data.resi || [];
        
        // Filtra per stato NC
        if (filterStato === 'in_attesa') {
          resiData = resiData.filter(r => !r.nota_credito_ricevuta);
        } else if (filterStato === 'ricevuta') {
          resiData = resiData.filter(r => r.nota_credito_ricevuta);
        }
        
        // Filtra per ricerca
        if (searchTerm) {
          const term = searchTerm.toLowerCase();
          resiData = resiData.filter(r => 
            r.fornitore?.toLowerCase().includes(term) ||
            r.prodotti?.some(p => p.nome?.toLowerCase().includes(term))
          );
        }
        
        setResi(resiData);
        setStatistiche(res.data.statistiche_fornitore || []);
      }
    } catch (error) {
      console.error('Errore caricamento resi:', error);
      
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResi();
  }, [filterFornitore, filterStato]);

  // Aggiorna stato nota credito
  const toggleNotaCredito = async (resoId, currentState) => {
    setUpdatingNC(resoId);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API}/gestione/resi-fornitore/${resoId}/nota-credito?nota_credito_ricevuta=${!currentState}`,
        {},
        { headers: getApiHeaders() }
      );
      
      if (res.data.success) {
        toast.success(currentState ? 'Nota credito rimossa' : 'Nota credito segnata come ricevuta');
        loadResi();
      }
    } catch (error) {
      console.error('Errore aggiornamento NC:', error);
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setUpdatingNC(null);
    }
  };

  // Calcola totali
  const totaleInAttesa = resi
    .filter(r => !r.nota_credito_ricevuta)
    .reduce((sum, r) => sum + (r.valore_totale || 0), 0);
  
  const totaleRicevute = resi
    .filter(r => r.nota_credito_ricevuta)
    .reduce((sum, r) => sum + (r.valore_totale || 0), 0);

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('it-IT');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-transparent lg:bg-gray-50">
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur-sm lg:bg-white border-b border-slate-800 lg:border-gray-200 p-4 sticky top-0 z-10">
        <div className="w-full">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.history.back()}
              className="text-slate-400 hover:text-white lg:text-gray-600 lg:hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white lg:text-gray-900">Resi Fornitore</h1>
              <p className="text-sm text-slate-400 lg:text-gray-500">Gestione note credito</p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Card className="bg-orange-500/20 border-orange-500/30 lg:bg-orange-50 lg:border-orange-200">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-400 lg:text-orange-600" />
                  <div>
                    <p className="text-xs text-orange-400 lg:text-orange-600">In Attesa NC</p>
                    <p className="text-lg font-bold text-orange-400 lg:text-orange-700">
                      {formatCurrency(totaleInAttesa)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-green-500/20 border-green-500/30 lg:bg-green-50 lg:border-green-200">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-400 lg:text-green-600" />
                  <div>
                    <p className="text-xs text-green-400 lg:text-green-600">NC Ricevute</p>
                    <p className="text-lg font-bold text-green-400 lg:text-green-700">
                      {formatCurrency(totaleRicevute)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-purple-500/20 border-purple-500/30 lg:bg-purple-50 lg:border-purple-200">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-purple-400 lg:text-purple-600" />
                  <div>
                    <p className="text-xs text-purple-400 lg:text-purple-600">Tot. Resi</p>
                    <p className="text-lg font-bold text-purple-400 lg:text-purple-700">
                      {resi.length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-blue-500/20 border-blue-500/30 lg:bg-blue-50 lg:border-blue-200">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <TrendingDown className="h-5 w-5 text-blue-400 lg:text-blue-600" />
                  <div>
                    <p className="text-xs text-blue-400 lg:text-blue-600">Fornitori</p>
                    <p className="text-lg font-bold text-blue-400 lg:text-blue-700">
                      {statistiche.length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filtri */}
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Cerca fornitore o prodotto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadResi()}
                className="pl-10 bg-slate-800 border-slate-700 text-white lg:bg-white lg:border-gray-300 lg:text-gray-900"
              />
            </div>
            
            <select
              value={filterStato}
              onChange={(e) => setFilterStato(e.target.value)}
              className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white lg:bg-white lg:border-gray-300 lg:text-gray-900"
            >
              <option value="tutti">Tutti gli stati</option>
              <option value="in_attesa">⏳ In attesa NC</option>
              <option value="ricevuta">✅ NC ricevuta</option>
            </select>
            
            <Button
              onClick={loadResi}
              variant="outline"
              className="border-slate-700 text-slate-300 lg:border-gray-300 lg:text-gray-700"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="w-full p-4">
        {/* Statistiche per Fornitore */}
        {statistiche.length > 0 && (
          <Card className="bg-slate-900/80 backdrop-blur-sm border-slate-800/50 lg:bg-white lg:border-gray-200 mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400 lg:text-gray-600">
                Riepilogo per Fornitore
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {statistiche.map((stat, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      filterFornitore === stat.fornitore
                        ? 'bg-purple-500/20 border-purple-500'
                        : 'bg-slate-800/50 border-slate-700 lg:bg-gray-50 lg:border-gray-200 hover:border-purple-500/50'
                    }`}
                    onClick={() => setFilterFornitore(filterFornitore === stat.fornitore ? '' : stat.fornitore)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-white lg:text-gray-900 text-sm">{stat.fornitore}</p>
                        <p className="text-xs text-slate-400 lg:text-gray-500">
                          {stat.numero_resi} resi totali
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-orange-400 lg:text-orange-600">
                          {formatCurrency(stat.totale_resi)}
                        </p>
                        <p className="text-xs text-slate-400 lg:text-gray-500">
                          {stat.senza_nota_credito > 0 ? (
                            <span className="text-orange-400">⏳ {stat.senza_nota_credito} in attesa</span>
                          ) : (
                            <span className="text-green-400">✅ Tutto OK</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lista Resi */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 text-purple-400 animate-spin mx-auto mb-2" />
            <p className="text-slate-400 lg:text-gray-500">Caricamento...</p>
          </div>
        ) : resi.length === 0 ? (
          <Card className="bg-slate-900/80 backdrop-blur-sm border-slate-800/50 lg:bg-white lg:border-gray-200">
            <CardContent className="text-center py-12">
              <Package className="h-12 w-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 lg:text-gray-500">
                {filterStato === 'in_attesa' 
                  ? 'Nessun reso in attesa di nota credito! 🎉'
                  : 'Nessun reso trovato'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {resi.map((reso) => (
              <Card 
                key={reso.id}
                className={`bg-slate-900/80 backdrop-blur-sm border-slate-800/50 lg:bg-white lg:border-gray-200 overflow-hidden ${
                  !reso.nota_credito_ricevuta ? 'border-l-4 border-l-orange-500' : 'border-l-4 border-l-green-500'
                }`}
              >
                <CardContent className="p-0">
                  {/* Header Reso */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-slate-800/50 lg:hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedReso(expandedReso === reso.id ? null : reso.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge className={`${
                          reso.nota_credito_ricevuta 
                            ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                            : 'bg-orange-500/20 text-orange-400 border-orange-500/30'
                        }`}>
                          {reso.nota_credito_ricevuta ? '✅ NC Ricevuta' : '⏳ In Attesa NC'}
                        </Badge>
                        <div>
                          <p className="font-medium text-white lg:text-gray-900">{reso.fornitore}</p>
                          <p className="text-xs text-slate-400 lg:text-gray-500">
                            DDT #{reso.ddt_numero || '-'} del {formatDate(reso.data_ddt)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-bold text-lg text-orange-400 lg:text-orange-600">
                            {formatCurrency(reso.valore_totale)}
                          </p>
                          <p className="text-xs text-slate-400 lg:text-gray-500">
                            {reso.prodotti?.length || 0} prodotti
                          </p>
                        </div>
                        {expandedReso === reso.id ? (
                          <ChevronUp className="h-5 w-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Dettaglio Reso (espanso) */}
                  {expandedReso === reso.id && (
                    <div className="border-t border-slate-800 lg:border-gray-200 p-4 bg-slate-800/30 lg:bg-gray-50">
                      {/* Prodotti */}
                      <div className="space-y-2 mb-4">
                        <p className="text-xs text-slate-400 lg:text-gray-500 uppercase tracking-wider">Prodotti Resi</p>
                        {reso.prodotti?.map((prod, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center justify-between p-2 bg-slate-900/50 lg:bg-white/90 backdrop-blur-sm rounded-lg"
                          >
                            <div>
                              <p className="text-sm font-medium text-white lg:text-gray-900">{prod.nome}</p>
                              <p className="text-xs text-slate-400 lg:text-gray-500">
                                {prod.quantita} {prod.unita_misura || 'kg'} • {prod.motivo || 'Motivo non specificato'}
                              </p>
                            </div>
                            <p className="font-medium text-orange-400 lg:text-orange-600">
                              {formatCurrency(prod.valore)}
                            </p>
                          </div>
                        ))}
                      </div>

                      {/* Azioni */}
                      <div className="flex items-center justify-between pt-3 border-t border-slate-700 lg:border-gray-200">
                        <p className="text-xs text-slate-400 lg:text-gray-500">
                          Registrato: {formatDate(reso.data_registrazione)}
                          {reso.data_nota_credito && (
                            <span className="ml-2 text-green-400">
                              • NC ricevuta: {formatDate(reso.data_nota_credito)}
                            </span>
                          )}
                        </p>
                        
                        <Button
                          size="sm"
                          onClick={() => toggleNotaCredito(reso.id, reso.nota_credito_ricevuta)}
                          disabled={updatingNC === reso.id}
                          className={`${
                            reso.nota_credito_ricevuta
                              ? 'bg-slate-700 hover:bg-slate-600 text-white'
                              : 'bg-green-600 hover:bg-green-700 text-white'
                          }`}
                        >
                          {updatingNC === reso.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                          ) : reso.nota_credito_ricevuta ? (
                            <X className="h-4 w-4 mr-2" />
                          ) : (
                            <Check className="h-4 w-4 mr-2" />
                          )}
                          {reso.nota_credito_ricevuta ? 'Rimuovi NC' : 'Segna NC Ricevuta'}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResiFornitorePage;
